import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loading, EmptyState } from "@/components/shared";
import { Search, Briefcase, MapPin, Banknote, Plus } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CareersIndex } from "./CareersIndex";

const DEPARTMENTS = ["Engineering", "Product", "Marketing", "Compliance", "Payments", "Data", "Operations"];
const MODELS = ["Remote", "Hybrid", "Onsite"];

export function salaryLabel(job) {
  if (job.salary_min == null || job.salary_max == null) return "Salary not stated";
  const fmt = (n) => new Intl.NumberFormat("en-GB", { style: "currency", currency: job.salary_currency || "EUR", maximumFractionDigits: 0 }).format(n);
  return `${fmt(job.salary_min)} - ${fmt(job.salary_max)} / ${job.salary_period === "month" ? "mo" : "yr"}`;
}

export default function Jobs() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [tab, setTab] = useState(params.get("tab") === "careers" ? "careers" : "roles");
  const [q, setQ] = useState("");
  const [market, setMarket] = useState(params.get("market") || "all");
  const [department, setDepartment] = useState("all");
  const [model, setModel] = useState("all");
  const [sort, setSort] = useState("recent");
  const [jobs, setJobs] = useState(null);
  const [markets, setMarkets] = useState([]);
  const isAdmin = user && (user.role === "admin" || user.role === "moderator");

  const load = (o = {}) => {
    setJobs(null);
    api.get("/jobs", { params: {
      search: o.search ?? q,
      market: (o.market ?? market) === "all" ? "" : (o.market ?? market),
      department: (o.department ?? department) === "all" ? "" : (o.department ?? department),
      working_model: (o.model ?? model) === "all" ? "" : (o.model ?? model),
      sort: o.sort ?? sort,
    } }).then(({ data }) => setJobs(data)).catch(() => setJobs([]));
  };

  useEffect(() => {
    load();
    api.get("/markets").then(({ data }) => setMarkets(data)).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const tabClass = (active) =>
    `rounded-full px-5 py-2 text-sm font-semibold transition-colors ${active ? "bg-[#12766B] text-white" : "text-slate-400 hover:text-ink"}`;

  return (
    <main className="mx-auto max-w-[1800px] px-6 py-12 sm:px-10 lg:px-16">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow mb-3">Careers</p>
          <h1 className="text-4xl font-bold">Jobs &amp; careers across iGaming</h1>
        </div>
        {isAdmin && tab === "roles" && (
          <Link to="/jobs/new" data-testid="post-job-link" className="btn-teal inline-flex items-center gap-2 text-sm"><Plus className="h-4 w-4" /> Post a vacancy</Link>
        )}
      </div>

      <div className="mt-6 inline-flex rounded-full border border-[#DDDAD1] bg-ink-900 p-1" role="tablist">
        <button data-testid="tab-roles" role="tab" aria-selected={tab === "roles"} onClick={() => setTab("roles")} className={tabClass(tab === "roles")}>Open roles</button>
        <button data-testid="tab-careers" role="tab" aria-selected={tab === "careers"} onClick={() => setTab("careers")} className={tabClass(tab === "careers")}>Careers index</button>
      </div>

      {tab === "roles" ? (
        <>
          <p className="mt-6 max-w-4xl text-slate-400">Live roles across iGaming employers. Some list a real salary range; others link straight to the employer's careers page.</p>

          <form onSubmit={(e) => { e.preventDefault(); load({ search: q }); }} className="mt-8 flex items-center gap-2 rounded-full border border-[#DDDAD1] bg-ink-900 p-1.5">
            <Search className="ml-3 h-5 w-5 text-slate-400" />
            <input data-testid="job-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search roles or companies…" className="min-w-0 flex-1 bg-transparent px-2 py-2 text-sm outline-none placeholder:text-slate-500" />
            <button className="btn-teal rounded-full text-sm">Search</button>
          </form>

          <div className="mt-4 grid grid-cols-2 gap-3 sm:flex sm:flex-wrap sm:items-center">
            <Select value={market} onValueChange={(v) => { setMarket(v); load({ market: v }); }}>
              <SelectTrigger data-testid="job-market" className="w-full min-w-0 border-[#DDDAD1] bg-ink-900 sm:w-44"><SelectValue placeholder="Hub" /></SelectTrigger>
              <SelectContent className="border-[#DDDAD1] bg-ink-900">
                <SelectItem value="all">All hubs</SelectItem>
                {markets.map((m) => <SelectItem key={m.market} value={m.market}>{m.market}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={department} onValueChange={(v) => { setDepartment(v); load({ department: v }); }}>
              <SelectTrigger data-testid="job-department" className="w-full min-w-0 border-[#DDDAD1] bg-ink-900 sm:w-44"><SelectValue placeholder="Department" /></SelectTrigger>
              <SelectContent className="border-[#DDDAD1] bg-ink-900">
                <SelectItem value="all">All departments</SelectItem>
                {DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={model} onValueChange={(v) => { setModel(v); load({ model: v }); }}>
              <SelectTrigger data-testid="job-model" className="w-full min-w-0 border-[#DDDAD1] bg-ink-900 sm:w-40"><SelectValue placeholder="Work style" /></SelectTrigger>
              <SelectContent className="border-[#DDDAD1] bg-ink-900">
                <SelectItem value="all">All work styles</SelectItem>
                {MODELS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={sort} onValueChange={(v) => { setSort(v); load({ sort: v }); }}>
              <SelectTrigger data-testid="job-sort" className="w-full min-w-0 border-[#DDDAD1] bg-ink-900 sm:w-44"><SelectValue /></SelectTrigger>
              <SelectContent className="border-[#DDDAD1] bg-ink-900">
                <SelectItem value="recent">Most recent</SelectItem>
                <SelectItem value="salary">Highest salary</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {jobs === null ? (
            <Loading label="Loading vacancies" />
          ) : jobs.length === 0 ? (
            <div className="mt-10"><EmptyState icon={Briefcase} title="No vacancies match" subtitle="Try widening your filters or check back soon." /></div>
          ) : (
            <div className="mt-10 space-y-4">
              {jobs.map((j, i) => (
                <Link key={j.id} to={`/job/${j.id}`} data-testid="job-card" className="card-surface lift flex flex-col gap-3 p-6 animate-fade-up sm:flex-row sm:items-center sm:justify-between" style={{ animationDelay: `${i * 30}ms` }}>
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-lg font-semibold text-ink">{j.title}</h3>
                      <span className="rounded-full bg-[#E7F0EE] px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.08em] text-teal-600">{j.department}</span>
                    </div>
                    <p className="mt-1 text-sm text-slate-400">{j.company_name}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-slate-500">
                      <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-teal-400" /> {j.market}</span>
                      <span>{j.working_model}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 rounded-full border border-teal-400/30 bg-teal-500/5 px-4 py-2 text-sm font-semibold text-teal-600">
                    <Banknote className="h-4 w-4" /> {salaryLabel(j)}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </>
      ) : (
        <CareersIndex />
      )}
    </main>
  );
}
